import { useState, FormEvent } from "react";
import { motion } from "framer-motion";
import { Anchor, CheckCircle, ArrowRight } from "lucide-react";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { usePageSEO } from "@/hooks/use-page-seo";

const NAVY = "#0B1622";

const SUNSET = "/creators/hero.jpg";

const steps = [
  {
    n: "01",
    title: "Apply",
    body: "Drop your info and your handles below — it takes about a minute. No follower minimum. We care that you create, not how big you are.",
  },
  {
    n: "02",
    title: "Get the nod",
    body: "We review applications every week and reach out by text and email to the creators we want on the crew.",
  },
  {
    n: "03",
    title: "Create & sail",
    body: "Post your Yacht Rock Detroit content, tag @yachtrockdetroit, and earn your passes. The more you create, the more you unlock.",
  },
];

const tiers = [
  { lvl: "You're in", prize: "2 GA", desc: "Get selected and post your first piece — you and a +1 are on the river." },
  { lvl: "Rolling", prize: "4 GA", desc: "Keep the content coming and bring the whole crew." },
  { lvl: "Buzzing", prize: "6 GA", desc: "Strong output and real traction — stack the passes." },
  { lvl: "Captain", prize: "2 VIP", desc: "Standout creators go full VIP — open bar, food, Admirals Lounge.", top: true },
];

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

const Star = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2l2.9 6.2 6.8.8-5 4.6 1.3 6.7L12 17.8 5.9 20.3 7.2 13.6l-5-4.6 6.8-.8z" />
  </svg>
);

const marqueeItems = [
  "2 Days on the River",
  "25+ Live Acts",
  "Captain Hats Encouraged",
  "Floating DJ Deck",
  "Sunset on the Water",
  "Now Casting Creators",
];

const Creators = () => {
  usePageSEO({
    title: "Become a Yacht Rock Detroit Creator",
    description:
      "Now casting Yacht Rock Detroit Creators. Make content, rep the weekend on the river, and earn your passes — up to VIP. July 17–18, 2026 at The Roostertail.",
    canonical: "https://yachtrockdetroit.com/creators",
    ogImage: "https://yachtrockdetroit.com/creators/poster.jpg",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const data = Object.fromEntries(new FormData(form).entries());
    // TODO: wire to n8n / GHL webhook to capture applications + trigger text/email.
    console.log("Creator application:", { ...data, source: "creator-apply" });
    setSubmitted(true);
    setTimeout(() => {
      document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const inputClass =
    "w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3.5 text-yacht-cream placeholder:text-white/30 text-[15px] outline-none transition focus:border-yacht-seaglass focus:ring-2 focus:ring-yacht-seaglass/25";
  const labelClass = "block text-[11px] font-bold uppercase tracking-[0.12em] text-white/55 mb-2";

  return (
    <div className="min-h-screen text-yacht-cream" style={{ backgroundColor: NAVY }}>
      <GlobalHeader />

      {/* HERO — desktop: text over the 16:9 crew image. Mobile: big text block, photo moved to the bottom in a wide 21:9 frame. */}
      <section className="relative flex flex-col md:flex-row md:items-center overflow-hidden md:min-h-0 md:aspect-[16/9]">
        {/* Desktop only: the crew image. */}
        <motion.div
          className="absolute inset-0 hidden md:block bg-cover bg-right"
          style={{ backgroundImage: `url(${SUNSET})` }}
          initial={{ scale: 1.15 }}
          animate={{ scale: 1.04 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        />
        {/* Desktop: left-to-dark gradient so the headline pops over the blank left side. */}
        <div
          className="absolute inset-0 hidden md:block"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,22,34,.94) 0%, rgba(11,22,34,.78) 38%, rgba(11,22,34,.30) 70%, rgba(11,22,34,.20) 100%)",
          }}
        />
        {/* Mobile: subtle glow behind the headline for depth. */}
        <div
          className="absolute inset-x-0 top-0 h-[70%] md:hidden pointer-events-none"
          style={{
            background:
              "radial-gradient(120% 80% at 50% 8%, rgba(56,159,186,.20), transparent 60%), radial-gradient(120% 60% at 50% 60%, rgba(168,85,247,.14), transparent 60%)",
          }}
        />

        <div className="relative z-10 w-full max-w-3xl mx-auto md:mx-0 px-6 md:pl-16 lg:pl-24 text-center md:text-left pt-28 md:pt-0 pb-10 md:pb-0">
          <div className="flex items-center justify-center md:justify-start gap-3 mb-7 md:mb-8 flex-wrap animate-fade-up">
            <span className="inline-flex items-center gap-1.5 bg-yacht-seaglass text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-sm px-4 py-2 rounded-full border-2 border-white/85 -rotate-2">
              <Anchor className="w-4 h-4" /> Now Casting
            </span>
            <span className="inline-flex items-center bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-sm px-4 py-2 rounded-full border-2 border-white/85 rotate-2">
              July 17–18<span className="hidden sm:inline"> · Detroit River</span>
            </span>
          </div>
          <h1 className="font-['Anton'] uppercase leading-[0.92] text-[clamp(2.75rem,12vw,4.6rem)] animate-fade-up">
            Become a<br />
            <span className="text-yacht-yellow sm:whitespace-nowrap">Yacht Rock Detroit</span><br />
            Creator
          </h1>
          <p className="mt-6 md:mt-7 mx-auto md:mx-0 max-w-xl text-lg md:text-lg text-yacht-cream/85 leading-relaxed animate-fade-up">
            We're casting a crew of Detroit creators to bring the festival to life. Make content, rep the weekend on the
            river — and earn your spot at the party.
          </p>
          <div className="mt-8 md:mt-10 animate-fade-up">
            <a
              href="#apply"
              className="inline-flex items-center gap-2 bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-lg md:text-base px-9 py-4 rounded-full shadow-[0_8px_0_#b5830f] hover:-translate-y-0.5 hover:shadow-[0_11px_0_#b5830f] active:translate-y-0 active:shadow-[0_4px_0_#b5830f] transition-all"
            >
              Apply to Create <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Mobile only: crew photo at the bottom in a wide 21:9 frame. */}
        <div className="md:hidden relative w-full aspect-[21/9] mt-2">
          <div
            className="absolute inset-0 bg-cover"
            style={{ backgroundImage: `url(${SUNSET})`, backgroundPosition: "center 28%" }}
          />
          {/* Top fade so the photo blends out of the navy headline area. */}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to bottom, #0B1622 0%, rgba(11,22,34,0) 32%, rgba(11,22,34,0) 100%)" }}
          />
        </div>
      </section>

      {/* MARQUEE */}
      <div className="relative overflow-hidden border-y border-white/10 bg-yacht-blue/[0.08] py-3.5">
        <motion.div
          className="flex whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
        >
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center shrink-0">
              {marqueeItems.map((item, i) => (
                <span key={`${copy}-${i}`} className="flex items-center">
                  <span className={`font-['Anton'] uppercase tracking-wide text-sm ${i % 2 === 0 ? "text-yacht-yellow" : "text-yacht-cream/70"}`}>
                    {item}
                  </span>
                  <Star className="w-3.5 h-3.5 mx-5 text-yacht-seaglass shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>

      {/* THE IDEA */}
      <section className="py-28 md:py-36 px-6">
        <motion.div {...fade} className="max-w-2xl mx-auto text-center">
          <div className="text-yacht-seaglass text-xs font-bold tracking-[0.22em] uppercase mb-5">The Idea</div>
          <h2 className="font-['Anton'] uppercase leading-[1] text-[clamp(1.9rem,4.4vw,3.2rem)]">
            A crew of creators, one <span className="text-yacht-yellow">unforgettable</span> weekend.
          </h2>
          <p className="mt-7 text-lg text-yacht-cream/75 leading-relaxed">
            Yacht Rock Detroit is two days of live music on the Detroit River — July 17–18, 2026. We're hand-picking a
            small crew of local creators to capture the vibe and share it with the city. You make the content you
            already love making. We get you on the river.
          </p>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 px-6 border-t border-white/[0.06]">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fade} className="text-center mb-16">
            <div className="text-yacht-seaglass text-xs font-bold tracking-[0.22em] uppercase mb-4">How it works</div>
            <h2 className="font-['Anton'] uppercase text-[clamp(1.8rem,4vw,3rem)]">
              Three steps to the <span className="text-yacht-yellow">deck.</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-12 md:gap-10">
            {steps.map((s, i) => (
              <motion.div key={s.n} {...fade} transition={{ ...fade.transition, delay: i * 0.08 }} className="text-center md:text-left">
                <div className="font-['Anton'] text-5xl text-yacht-blue/90 mb-4">{s.n}</div>
                <h3 className="font-['Anton'] uppercase tracking-wide text-xl mb-3">{s.title}</h3>
                <p className="text-yacht-cream/70 leading-relaxed text-[15px]">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU EARN */}
      <section className="py-20 md:py-28 px-6 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade} className="text-center mb-16 max-w-xl mx-auto">
            <div className="text-yacht-seaglass text-xs font-bold tracking-[0.22em] uppercase mb-4">What you earn</div>
            <h2 className="font-['Anton'] uppercase text-[clamp(1.8rem,4vw,3rem)]">
              Create more. <span className="text-yacht-yellow">Unlock more.</span>
            </h2>
            <p className="mt-5 text-yacht-cream/70 leading-relaxed">
              Every pass is a full two-day weekend pass. Get on the crew and you're in — then keep creating to climb.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((t, i) => (
              <motion.div
                key={t.lvl}
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.07 }}
                className={`relative rounded-2xl p-7 ${
                  t.top
                    ? "bg-yacht-yellow/[0.06] border border-yacht-yellow/60"
                    : "bg-white/[0.03] border border-white/10"
                }`}
              >
                {t.top && (
                  <span className="absolute -top-3 right-5 bg-yacht-yellow text-yacht-charcoal text-[10px] font-['Anton'] uppercase tracking-wide px-3 py-1 rounded-full">
                    Top crew
                  </span>
                )}
                <div className={`text-[11px] font-bold uppercase tracking-[0.14em] mb-3 ${t.top ? "text-yacht-yellow" : "text-yacht-seaglass"}`}>
                  {t.lvl}
                </div>
                <div className="font-['Anton'] text-4xl leading-none">{t.prize}</div>
                <div className="text-yacht-yellow text-[13px] font-semibold mt-1 mb-4">Weekend passes</div>
                <p className="text-yacht-cream/65 text-[14px] leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VIBE BAND */}
      <section className="relative min-h-[42vh] flex items-center justify-center text-center overflow-hidden border-y border-white/[0.06]">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(/creators/vibe-bg.jpg)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(rgba(11,22,34,.45), rgba(11,22,34,.78))" }} />
        <motion.div {...fade} className="relative z-10 px-6 py-20">
          <h2 className="font-['Anton'] uppercase leading-[0.95] text-[clamp(2rem,5vw,3.6rem)] drop-shadow-lg">
            This could be<br />your <span className="text-yacht-yellow">weekend.</span>
          </h2>
          <a
            href="#apply"
            className="inline-flex items-center gap-2 mt-8 bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide px-8 py-3.5 rounded-full shadow-[0_8px_0_#b5830f] hover:-translate-y-0.5 hover:shadow-[0_11px_0_#b5830f] active:translate-y-0 active:shadow-[0_4px_0_#b5830f] transition-all"
          >
            Apply to Create <ArrowRight className="w-5 h-5" />
          </a>
        </motion.div>
      </section>

      {/* APPLY */}
      <section id="apply" className="py-24 md:py-32 px-6 scroll-mt-24">
        <div className="max-w-2xl mx-auto">
          {!submitted ? (
            <motion.div {...fade}>
              <div className="text-center mb-12">
                <div className="text-yacht-seaglass text-xs font-bold tracking-[0.22em] uppercase mb-4">Now Casting</div>
                <h2 className="font-['Anton'] uppercase text-[clamp(1.9rem,4.4vw,3.2rem)]">
                  Apply to be a <span className="text-yacht-yellow">YRD Creator</span>
                </h2>
                <p className="mt-5 text-yacht-cream/70 leading-relaxed max-w-md mx-auto">
                  Fill this out and you're in the running. We review weekly and reach out by text + email if you're
                  selected.
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>First name *</label>
                    <input name="firstName" required placeholder="First" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Last name *</label>
                    <input name="lastName" required placeholder="Last" className={inputClass} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Date of birth *</label>
                    <input name="dob" type="date" required className={`${inputClass} [color-scheme:dark]`} />
                  </div>
                  <div>
                    <label className={labelClass}>City *</label>
                    <input name="city" required placeholder="e.g. Detroit, MI" className={inputClass} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Email *</label>
                    <input name="email" type="email" required placeholder="you@email.com" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Phone *</label>
                    <input name="phone" type="tel" required placeholder="(313) 555-0123" className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Instagram handle *</label>
                  <div className="flex items-center bg-white/[0.04] border border-white/15 rounded-xl overflow-hidden focus-within:border-yacht-seaglass focus-within:ring-2 focus-within:ring-yacht-seaglass/25 transition">
                    <span className="px-3.5 text-white/45 border-r border-white/12">@</span>
                    <input name="instagram" required placeholder="yourhandle" className="flex-1 bg-transparent px-3.5 py-3.5 text-yacht-cream placeholder:text-white/30 text-[15px] outline-none" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>TikTok <span className="text-white/35 font-medium normal-case tracking-normal">(optional)</span></label>
                    <div className="flex items-center bg-white/[0.04] border border-white/15 rounded-xl overflow-hidden focus-within:border-yacht-seaglass focus-within:ring-2 focus-within:ring-yacht-seaglass/25 transition">
                      <span className="px-3.5 text-white/45 border-r border-white/12">@</span>
                      <input name="tiktok" placeholder="yourhandle" className="flex-1 bg-transparent px-3.5 py-3.5 text-yacht-cream placeholder:text-white/30 text-[15px] outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Facebook <span className="text-white/35 font-medium normal-case tracking-normal">(optional)</span></label>
                    <input name="facebook" placeholder="Profile name or link" className={inputClass} />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-lg py-4 rounded-xl shadow-[0_8px_0_#b5830f] hover:-translate-y-0.5 hover:shadow-[0_11px_0_#b5830f] active:translate-y-0 active:shadow-[0_4px_0_#b5830f] transition-all mt-2"
                >
                  Apply to Create
                </button>
                <p className="text-center text-[12.5px] text-white/45 leading-relaxed">
                  We review applications every week and reach out by <span className="text-yacht-seaglass">text + email</span> if you're selected.
                  Must be 18+. Tag <span className="text-yacht-seaglass">@yachtrockdetroit</span> in your content.
                </p>
              </form>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10">
              <CheckCircle className="w-14 h-14 text-yacht-seaglass mx-auto mb-5" />
              <h2 className="font-['Anton'] uppercase text-3xl text-yacht-seaglass">You're in the running!</h2>
              <p className="mt-4 text-yacht-cream/80 leading-relaxed max-w-md mx-auto">
                Thanks for applying to be a Yacht Rock Detroit Creator. We review every week — keep an eye on your texts
                and email.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Creators;
